import React, { useMemo } from 'react';
import { Flex, Skeleton, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { Device } from 'clipsal-cortex-types/src/api';

import { useSiteCapabilityCheck } from '../../../../common/hooks/use-site-capability-check';
import { useGetFirmwareVersion } from '../../accountApi';
import { HardwareConnection } from '../types';

export function DetailsInfo({ device, connection }: { device: Device; connection?: HardwareConnection }) {
  const { t } = useTranslation();
  const { siteHasInverter } = useSiteCapabilityCheck();

  const hasFirmware = useMemo(
    () => siteHasInverter && ['METER', 'INVERTER'].includes(device.device_type),
    [siteHasInverter, device]
  );

  const { data, isLoading } = useGetFirmwareVersion(Number(device.id), !hasFirmware);

  const { iccid, imei, firmware, manufacturer, model, serial } = useMemo(() => {
    const deviceSerial = device.serial_number ?? device.site_identifier;
    const deviceFirmware = hasFirmware ? data?.firmware_version : undefined;
    const isCellular = device.device_type === 'CELLULAR_MODEM';
    const connectionSerial = connection && isCellular ? connection.serial_number : undefined;
    const connectionFirmware = connection && isCellular ? connection.firmware_version : undefined;
    const connectionModel = connection && isCellular ? connection.model_name : undefined;

    return {
      serial: connectionSerial ?? deviceSerial ?? undefined,
      model: connectionModel ?? device.model,
      manufacturer: device ? device.manufacturer_name ?? 'Schneider Electric' : undefined,
      firmware: connectionFirmware ?? deviceFirmware ?? undefined,
      imei: connection && isCellular ? connection.imei : undefined,
      iccid: connection && isCellular ? connection.iccid : undefined,
    };
  }, [device, hasFirmware, data?.firmware_version, connection]);

  return (
    <Flex direction={'column'} w="100%" data-testid="device-details" mt={8}>
      <Flex direction={'column'}>
        {serial && (
          <Flex direction={'column'} data-testid="device-sn">
            <Text fontWeight={'semibold'}>{t('Common.serial number')}</Text>
            <Text>{serial}</Text>
          </Flex>
        )}

        {model && (
          <Flex direction={'column'} mt={6} data-testid="device-model">
            <Text fontWeight={'semibold'}>{t('Common.model name')}</Text>
            <Text>{model}</Text>
          </Flex>
        )}

        {manufacturer && (
          <Flex direction={'column'} mt={6} data-testid="device-manufacturer">
            <Text fontWeight={'semibold'}>{t('Hardware.manufacturer')}</Text>
            <Text>{manufacturer}</Text>
          </Flex>
        )}

        {/* Firmware version is only supported by Saturn inverters currently. Meter firmware version can only
                  be retrieved when the meter is connected to an inverter (not standalone) */}
        {firmware && (
          <Flex direction={'column'} mt={6} data-testid="device-firmware">
            <Text fontWeight={'semibold'}>{t('Common.firmware')}</Text>
            <Skeleton isLoaded={!isLoading}>
              <Text>{firmware}</Text>
            </Skeleton>
          </Flex>
        )}

        {imei && (
          <Flex direction={'column'} mt={6} data-testid="device-imei">
            <Text fontWeight={'semibold'}>IMEI</Text>
            <Text>{imei}</Text>
          </Flex>
        )}

        {iccid && (
          <Flex direction={'column'} mt={6} data-testid="device-simid">
            <Text fontWeight={'semibold'}>SIM ID</Text>
            <Text>{iccid}</Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
